import type { TypedDocumentNode, WatchQueryFetchPolicy } from '@apollo/client';
import { gql } from '@apollo/client';

import { PaymentMethodProvider } from '#src/__generated__/globalTypes.ts';
import { CreditCard } from '#src/components/buyActions/CreditCard/index.tsx';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useMangopayCreditCardsEnabled } from '#src/hooks/useMangopayCreditCardsEnabled.ts';

import type {
  CurrentUserPaymentMethodsQuery,
  CurrentUserPaymentMethodsQueryVariables,
} from './__generated__/useCurrentUserPaymentMethods.graphql.ts';

const CURRENT_USER_PAYMENT_METHODS_QUERY = gql`
  query CurrentUserPaymentMethodsQuery {
    currentUser {
      slug
      stripePaymentMethods: paymentMethods(provider: STRIPE) {
        id
        card {
          ...CreditCard_creditCard
        }
      }
      mangopayPaymentMethods: paymentMethods(provider: MANGOPAY) {
        id
        card {
          ...CreditCard_creditCard
        }
      }
    }
  }
  ${CreditCard.fragments.creditCard}
` as TypedDocumentNode<
  CurrentUserPaymentMethodsQuery,
  CurrentUserPaymentMethodsQueryVariables
>;

export const useCurrentUserPaymentMethods = (
  fetchPolicy?: WatchQueryFetchPolicy
) => {
  const useMangopayCreditCards = useMangopayCreditCardsEnabled();

  const { data } = useQuery(CURRENT_USER_PAYMENT_METHODS_QUERY, {
    fetchPolicy: fetchPolicy || 'cache-and-network',
  });

  if (!data?.currentUser) return [];

  const {
    currentUser: { stripePaymentMethods, mangopayPaymentMethods },
  } = data;

  return [
    ...stripePaymentMethods.map(m => ({
      provider: PaymentMethodProvider.STRIPE,
      disabled: useMangopayCreditCards,
      ...m,
    })),
    ...mangopayPaymentMethods.map(m => ({
      provider: PaymentMethodProvider.MANGOPAY,
      disabled: false,
      ...m,
    })),
  ];
};
